import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Calculator, 
  Book, 
  Microscope, 
  Languages, 
  Download, 
  GraduationCap, 
  Building2, 
  Users, 
  Clock, 
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

type Course = {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
  subjects: string[];
  duration: string;
  careers: string[];
  image: string;
};

const courses: Course[] = [
  {
    id: 'ciencias-tecnologias',
    title: 'Ciências e Tecnologias',
    icon: Calculator,
    description: 'O curso de Ciências e Tecnologias prepara os alunos para o prosseguimento de estudos em áreas como Engenharia, Medicina, Ciências, Tecnologia e outras áreas científicas.',
    subjects: [
      'Matemática A',
      'Física e Química A',
      'Biologia e Geologia',
      'Geometria Descritiva A',
      'Aplicações Informáticas B'
    ],
    duration: '3 anos letivos',
    careers: [
      'Medicina',
      'Engenharia',
      'Arquitetura',
      'Biotecnologia',
      'Investigação Científica'
    ],
    image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
  },
  {
    id: 'linguas-humanidades',
    title: 'Línguas e Humanidades',
    icon: Languages,
    description: 'O curso de Línguas e Humanidades oferece uma formação sólida em línguas, literatura, história e outras ciências sociais, preparando os alunos para diversas áreas das humanidades.',
    subjects: [
      'Português',
      'História A',
      'Geografia A',
      'MACS',
      'Língua Estrangeira'
    ],
    duration: '3 anos letivos',
    careers: [
      'Direito',
      'Jornalismo',
      'Psicologia',
      'Relações Internacionais',
      'Tradução'
    ],
    image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
  },
  {
    id: 'socioeconomicas',
    title: 'Ciências Socioeconómicas',
    icon: Book,
    description: 'O curso de Ciências Socioeconómicas proporciona uma formação aprofundada em economia, matemática e geografia, preparando os alunos para carreiras nas áreas de economia, gestão e finanças.',
    subjects: [
      'Matemática A',
      'Economia A',
      'Geografia A',
      'História B',
      'Sociologia'
    ],
    duration: '3 anos letivos',
    careers: [
      'Economia',
      'Gestão',
      'Contabilidade',
      'Marketing',
      'Finanças'
    ],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
  },
  {
    id: 'artes-visuais',
    title: 'Artes Visuais',
    icon: Microscope,
    description: 'O curso de Artes Visuais desenvolve competências artísticas e criativas, preparando os alunos para carreiras nas áreas das artes, design e multimédia.',
    subjects: [
      'Desenho A',
      'Geometria Descritiva A',
      'História da Cultura e das Artes',
      'Oficina de Artes',
      'Oficina Multimédia'
    ],
    duration: '3 anos letivos',
    careers: [
      'Design',
      'Artes Plásticas',
      'Arquitetura',
      'Multimédia',
      'Cinema e Audiovisual'
    ],
    image: 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
  }
];

const ScientificCourses = () => {
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-background to-secondary/20">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative mb-12"
      >
        <div
          className="h-[60vh] relative flex items-center justify-center"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8)), url("https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
            backgroundAttachment: 'fixed',
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Cursos Científico-Humanísticos</h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
              Explore os cursos científico-humanísticos do AEAAV e prepare-se para o ensino superior.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Courses Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid gap-8 md:grid-cols-2"
        >
          {courses.map((course) => (
            <motion.div
              key={course.id}
              variants={itemVariants}
              className="group bg-card rounded-lg overflow-hidden hover:ring-1 hover:ring-primary/50 transition-all cursor-pointer"
              onClick={() => setSelectedCourse(course)}
            >
              <div
                className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                style={{ backgroundImage: `url(${course.image})` }}
              />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <course.icon className="w-6 h-6 text-primary" />
                  <h2 className="text-xl font-semibold group-hover:text-primary transition-colors">
                    {course.title}
                  </h2>
                </div>
                <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                  {course.description}
                </p>
                <button className="text-primary hover:text-primary/80 text-sm font-medium transition-colors">
                  Saber mais →
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Course Details Modal */}
      {selectedCourse && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCourse(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="h-64 bg-cover bg-center relative"
              style={{ backgroundImage: `url(${selectedCourse.image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <selectedCourse.icon className="w-8 h-8 text-primary" />
                  <h2 className="text-2xl font-bold">{selectedCourse.title}</h2>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Course Info */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-primary" />
                  Sobre o Curso
                </h3>
                <p className="text-gray-300">{selectedCourse.description}</p>
              </div>

              {/* Course Details */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    Disciplinas Específicas
                  </h3>
                  <ul className="space-y-2">
                    {selectedCourse.subjects.map((subject, index) => (
                      <li key={index} className="flex items-center gap-2 text-gray-300">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {subject}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Saídas Profissionais
                  </h3>
                  <ul className="space-y-2">
                    {selectedCourse.careers.map((career, index) => (
                      <li key={index} className="flex items-center gap-2 text-gray-300">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {career}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Course Duration */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <h4 className="font-medium mb-1">Duração</h4>
                    <p className="text-sm text-gray-300">{selectedCourse.duration}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <h4 className="font-medium mb-1">Acesso ao Ensino Superior</h4>
                    <p className="text-sm text-gray-300">Exames nacionais específicos</p>
                  </div>
                </div>
              </div>

              {/* Download Button */}
              <div className="flex justify-center pt-4">
                <button className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors">
                  <Download className="w-4 h-4" />
                  Descarregar Folheto Informativo
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ScientificCourses;